import { IMAGES, ATTRACTION_MAPS } from "@/lib/site";
import { MapPin, Mountain, ExternalLink, Waves } from "lucide-react";

const attractions = [
  {
    name: "Watehole Dam",
    desc: "A hidden gem near Sakleshpur — a peaceful dam with stunning sunset views. Our host Harsha often takes guests here in his car for a beautiful evening.",
    img: IMAGES.wateholeDam,
    tag: "Sunset Spot",
    tagIcon: Waves,
    maps: ATTRACTION_MAPS.watehole,
  },
  {
    name: "Parvathamma Betta",
    desc: "A short scenic trek to the top of Parvathamma Hill — one of the most beautiful sunrise / sunset viewpoints in the region.",
    img: IMAGES.parvathammaBetta,
    tag: "Trekking",
    tagIcon: Mountain,
    maps: ATTRACTION_MAPS.parvathamma,
  },
  {
    name: "Yagachi Water Sports",
    desc: "Popular Yagachi reservoir near Hassan offering kayaking, boating and jet-ski rides — a fun day-trip for families and groups.",
    img: IMAGES.yagachi,
    tag: "Adventure",
    tagIcon: Waves,
    maps: ATTRACTION_MAPS.yagachi,
  },
  {
    name: "Belur Temple",
    desc: "12th-century Chennakeshava Temple — a masterpiece of Hoysala stone carving and one of Karnataka's most iconic heritage sites.",
    img: IMAGES.belurTemple,
    tag: "Heritage",
    tagIcon: MapPin,
    maps: ATTRACTION_MAPS.belur,
  },
  {
    name: "Halebeedu Temple",
    desc: "The historic Hoysaleswara temple with intricate stone reliefs depicting scenes from Hindu mythology.",
    img: IMAGES.halebidu,
    tag: "Heritage",
    tagIcon: MapPin,
    maps: ATTRACTION_MAPS.halebidu,
  },
  {
    name: "Manjarabad Fort",
    desc: "The star-shaped hill fort built by Tipu Sultan in Sakleshpur — panoramic Western Ghats views from the top.",
    img: IMAGES.manjarabad,
    tag: "Fort",
    tagIcon: MapPin,
    maps: ATTRACTION_MAPS.manjarabad,
  },
];

const Attractions = () => {
  return (
    <section
      id="attractions"
      data-testid="attractions-section"
      className="relative py-24 md:py-32 bg-[#F8FAF7]"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="max-w-2xl reveal">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-8 bg-[#D4AF37]" />
            <span className="text-[11px] tracking-[0.3em] uppercase text-[#D4AF37] font-medium">
              Nearby Attractions
            </span>
          </div>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl text-[#1B4332] leading-[1.05] tracking-tight">
            Explore the{" "}
            <span className="italic font-light">Malnad Wonders</span>
          </h2>
          <p className="mt-5 text-[#1A1A1A]/75 text-base md:text-lg leading-relaxed">
            Navilukallu sits at the crossroads of Karnataka's most beautiful
            heritage sites, treks and reservoirs — perfect for day trips.
          </p>
        </div>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {attractions.map((a, i) => (
            <article
              key={a.name}
              className="reveal group relative rounded-3xl overflow-hidden bg-[#1B4332] aspect-[4/5] shadow-xl hover:-translate-y-1 transition-transform duration-500"
              style={{ animationDelay: `${i * 0.06}s` }}
              data-testid={`attraction-card-${i}`}
            >
              <img
                src={a.img}
                alt={a.name}
                loading="lazy"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0f0f0f]/95 via-[#1B4332]/40 to-transparent" />
              <div className="absolute inset-0 p-6 flex flex-col justify-end">
                <div className="flex items-center gap-2 text-[#D4AF37] text-[10px] tracking-[0.25em] uppercase mb-3">
                  <a.tagIcon size={13} strokeWidth={1.8} />
                  {a.tag}
                </div>
                <h3 className="font-heading text-2xl md:text-[26px] text-white leading-tight">
                  {a.name}
                </h3>
                <p className="mt-2 text-white/80 text-sm leading-relaxed">
                  {a.desc}
                </p>
                <a
                  href={a.maps}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`attraction-maps-${i}`}
                  className="mt-4 self-start inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/95 text-[#1B4332] text-xs font-medium hover:bg-[#D4AF37] hover:text-[#1A1A1A] transition-colors"
                >
                  <MapPin size={13} strokeWidth={2} />
                  View on Google Maps
                  <ExternalLink size={12} />
                </a>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Attractions;
